import React from "react";
import { motion } from "framer-motion";
import svgPaths from "./svg/svg-ctbt65jd2r";

/* ─── Stacked Bar Chart ─── */
function StackedBar({ segments, height }) {
  return (
    <div className="flex flex-col items-start flex-shrink-0 relative z-10" style={{ width: 54, height }}>
      {segments.map((s, i) => (
        <div key={i} style={{ background: s.color, flex: s.flex, width: "100%", minHeight: 1 }} />
      ))}
    </div>
  );
}

function BarChart() {
  const bars = [
    { height: 181, segments: [{ color: "#09485d", flex: 64 }, { color: "#75e799", flex: 62 }, { color: "#68055d", flex: 55 }] },
    { height: 186, segments: [{ color: "#09485d", flex: 52 }, { color: "#75e799", flex: 57 }, { color: "#68055d", flex: 77 }] },
    { height: 133, segments: [{ color: "#09485d", flex: 33 }, { color: "#75e799", flex: 43 }, { color: "#68055d", flex: 57 }] },
    { height: 163, segments: [{ color: "#09485d", flex: 33 }, { color: "#75e799", flex: 43 }, { color: "#68055d", flex: 87 }] },
  ];
  const labels = ["Competitiveness", "Player skill", "Excitement", "Ticket prices"];

  return (
    <div className="flex flex-col items-center" style={{ gap: 15 }}>
      <div className="relative flex items-end" style={{ gap: 39, height: 186 }}>
        {/* Grid lines */}
        <div className="absolute pointer-events-none z-0" style={{ left: -24, right: -24, top: 0, bottom: 0 }}>
          <div className="absolute w-full" style={{ background: "rgba(0,0,0,0.04)", height: 1, top: "40%" }} />
          <div className="absolute w-full" style={{ background: "rgba(0,0,0,0.04)", height: 1, top: "65%" }} />
          <div className="absolute w-full" style={{ background: "rgba(0,0,0,0.04)", height: 1, bottom: 0 }} />
        </div>
        {bars.map((bar, i) => (
          <StackedBar key={i} segments={bar.segments} height={bar.height} />
        ))}
      </div>
      <div className="flex items-start justify-center" style={{ gap: 39, width: 343 }}>
        {labels.map((label) => (
          <p key={label} style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.39, color: "#898989", textAlign: "center", width: 54 }}>{label}</p>
        ))}
      </div>
    </div>
  );
}

/* ─── Barriers Table ─── */
function BarriersSection() {
  return (
    <div className="flex flex-col items-start flex-shrink-0 w-full" style={{ gap: 3.37, paddingTop: 14.56, borderRadius: 2.43, position: "relative" }}>
      <div className="absolute pointer-events-none" style={{ inset: -0.61, border: "0.606px solid #e5e7e9", borderRadius: 3.03 }} />
      {/* Header */}
      <div className="w-full" style={{ padding: "0 14.56px" }}>
        <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 8.49, color: "#323945" }}>Significant barriers to enjoyment</p>
      </div>
      {/* Separator */}
      <div style={{ width: "100%", height: 0.61 }} />
      {/* Row: High ticket prices */}
      <div className="w-full" style={{ background: "rgba(255,255,255,0.08)", borderRadius: 2.43, position: "relative" }}>
        <div className="absolute pointer-events-none" style={{ inset: 0, borderTop: "0.606px solid #e5e7e9", borderRadius: 2.43 }} />
        <div className="flex items-center justify-between" style={{ padding: "14.56px" }}>
          <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: 8.49, color: "#323945" }}>High ticket prices</p>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Report Card ─── */
export function ReportCard() {
  return (
    <div
      className="overflow-hidden"
      style={{
        background: "white",
        borderRadius: 10,
        width: 454,
        height: 577,
        position: "relative",
        boxShadow: "0px 3.553px 92.389px 0px rgba(17,2,35,0.12)",
      }}
    >
      <motion.div
        className="flex flex-col items-start w-full"
        style={{ padding: "25.17px 29px 25.17px", gap: 15.68 }}
        animate={{ y: [0, -680] }}
        transition={{ delay: 1, duration: 4.5, ease: "easeInOut" }}
      >
        {/* Title */}
        <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 13.05, lineHeight: "17.25px", color: "#323945", width: "100%" }}>
          Major League Baseball Fan Engagement &amp; Experience
        </p>

        {/* Chapter 1 */}
        <div className="flex flex-col items-start w-full" style={{ gap: 5.04 }}>
          {/* Chapter heading */}
          <div className="flex items-center w-full" style={{ gap: 9.7, height: 20.62 }}>
            <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: 7.28, lineHeight: "20.62px", color: "#323945", textTransform: "uppercase", whiteSpace: "nowrap" }}>Chapter 1</p>
            <div style={{ flex: 1, height: 0.61, background: "#e8ebef" }} />
          </div>

          {/* Section content */}
          <div className="flex flex-col items-start w-full" style={{ gap: 14.56 }}>
            {/* Subtitle */}
            <div className="flex flex-col items-start w-full" style={{ gap: 9.7 }}>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 12.13, lineHeight: "16.78px", color: "#323945" }}>
                <p style={{ margin: 0 }}>Game Excitement and Player Skill Drive Enjoyment—</p>
                <p style={{ margin: 0 }}>Affordability Is a Key Barrier</p>
              </div>
              <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: 9.7, lineHeight: "14.56px", color: "#323945" }}>
                The enjoyment of MLB games is most strongly driven by the excitement of the game and the skill of the players. When asked to rank factors important to their enjoyment, fans consistently placed excitement and player skill at the top, with atmosphere and social aspects ranking lower. This prioritization is consistent across age groups and parental status, indicating a universal desire for dynamic, high-quality play.
              </p>
            </div>

            {/* Chart card */}
            <div className="relative w-full" style={{ borderRadius: 2.43, background: "rgba(255,255,255,0.2)" }}>
              <div className="absolute pointer-events-none" style={{ inset: 0, border: "0.606px solid #e5e7e9", borderRadius: 2.43 }} />
              <div className="flex flex-col items-center" style={{ padding: "14.56px 14.91px 16.42px", gap: 28.28 }}>
                {/* Chart title */}
                <div className="w-full">
                  <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 8.49, color: "#190122" }}>Satisfaction with MLB Aspects</p>
                </div>
                <BarChart />
              </div>
            </div>

            {/* Barriers */}
            <BarriersSection />
          </div>
        </div>

        {/* Chapter 2 */}
        <div className="flex flex-col items-start w-full" style={{ gap: 5.04, marginTop: 12 }}>
          {/* Chapter heading */}
          <div className="flex items-center w-full" style={{ gap: 9.7, height: 20.62 }}>
            <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: 7.28, lineHeight: "20.62px", color: "#323945", textTransform: "uppercase", whiteSpace: "nowrap" }}>Chapter 2</p>
            <div style={{ flex: 1, height: 0.61, background: "#e8ebef" }} />
          </div>

          <div className="flex flex-col items-start w-full" style={{ gap: 14.56 }}>
            {/* Subtitle */}
            <div className="flex flex-col items-start w-full" style={{ gap: 9.7 }}>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 12.13, lineHeight: "16.78px", color: "#323945" }}>
                <p style={{ margin: 0 }}>Stadium Attendance Frequency and Demographics</p>
              </div>
              <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: 9.7, lineHeight: "14.56px", color: "#323945" }}>
                Our survey reveals attendance trends vary significantly depending on household income and geographic proximity. Over 65% of regular game attendees earn above the national median income, reinforcing the affordability barrier noted earlier.
              </p>
            </div>

            {/* Data Table */}
            <div className="w-full flex flex-col" style={{ border: "0.606px solid #e5e7e9", borderRadius: 4, overflow: "hidden" }}>
              <div className="flex bg-[#f4f5f6]" style={{ padding: "8px 12px", borderBottom: "0.606px solid #e5e7e9" }}>
                <p style={{ flex: 1, fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 8.5, color: "#323945" }}>Demographic Segment</p>
                <p style={{ width: 80, fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 8.5, color: "#323945", textAlign: "right" }}>Attendance (%)</p>
                <p style={{ width: 80, fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 8.5, color: "#323945", textAlign: "right" }}>Avg. Spend</p>
              </div>
              {[
                { label: "18-24 years", val1: "15%", val2: "$45" },
                { label: "25-34 years", val1: "28%", val2: "$85" },
                { label: "35-44 years", val1: "32%", val2: "$120" },
                { label: "45+ years", val1: "25%", val2: "$95" }
              ].map((row, i) => (
                <div key={i} className="flex" style={{ padding: "8px 12px", borderBottom: i !== 3 ? "0.606px solid #e5e7e9" : "none" }}>
                  <p style={{ flex: 1, fontFamily: "'Roboto', sans-serif", fontSize: 8.5, color: "#323945" }}>{row.label}</p>
                  <p style={{ width: 80, fontFamily: "'Roboto', sans-serif", fontSize: 8.5, color: "#323945", textAlign: "right" }}>{row.val1}</p>
                  <p style={{ width: 80, fontFamily: "'Roboto', sans-serif", fontSize: 8.5, color: "#323945", textAlign: "right" }}>{row.val2}</p>
                </div>
              ))}
            </div>

            {/* Simple Progress Bars */}
            <div className="w-full flex flex-col" style={{ gap: 12, padding: "16px", background: "rgba(255,255,255,0.2)", border: "0.606px solid #e5e7e9", borderRadius: 4 }}>
              <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 8.49, color: "#190122" }}>Key Takeaways Matrix</p>
              {[
                { label: "Value perception", pct: 40 },
                { label: "Entertainment quality", pct: 85 },
                { label: "Food & Beverage ratings", pct: 60 }
              ].map((item, i) => (
                <div key={i} className="flex flex-col" style={{ gap: 4 }}>
                  <div className="flex justify-between">
                    <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, color: "#323945" }}>{item.label}</span>
                    <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, color: "#323945", fontWeight: 500 }}>{item.pct}%</span>
                  </div>
                  <div className="w-full bg-[#e8ebef]" style={{ height: 6, borderRadius: 3 }}>
                    <div style={{ width: `${item.pct}%`, height: "100%", background: "#68055d", borderRadius: 3 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Chapter 3 */}
        <div className="flex flex-col items-start w-full" style={{ gap: 5.04, marginTop: 12 }}>
          <div className="flex items-center w-full" style={{ gap: 9.7, height: 20.62 }}>
            <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: 7.28, lineHeight: "20.62px", color: "#323945", textTransform: "uppercase", whiteSpace: "nowrap" }}>Chapter 3</p>
            <div style={{ flex: 1, height: 0.61, background: "#e8ebef" }} />
          </div>

          <div className="flex flex-col items-start w-full" style={{ gap: 14.56 }}>
            <div className="flex flex-col items-start w-full" style={{ gap: 9.7 }}>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 12.13, lineHeight: "16.78px", color: "#323945" }}>
                <p style={{ margin: 0 }}>Digital Engagement & Future Recommendations</p>
              </div>
              <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: 9.7, lineHeight: "14.56px", color: "#323945" }}>
                Fans are increasingly seeking digital enhancements to their live experience. Implementing robust mobile-ordering solutions, real-time interactive replays, and augmented reality (AR) stat-tracking have shown a potential 40% increase in secondary engagement scores.
              </p>
            </div>

            {/* Recommendations Blocks */}
            <div className="flex flex-col w-full" style={{ gap: 8 }}>
              {[
                { title: "Enhance Mobile App Utility", desc: "Prioritize in-seat ordering and digital ticketing features." },
                { title: "Tiered Pricing Strategies", desc: "Introduce dynamic ticketing to ease affordability barriers." },
                { title: "Loyalty Programs", desc: "Gamify fan engagement across digital and physical touchpoints." }
              ].map((rec, i) => (
                <div key={i} className="flex flex-col" style={{ padding: "12px", background: "#f8f9fa", borderLeft: "3px solid #09485d", borderRadius: "0 4px 4px 0" }}>
                  <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 600, fontSize: 9.5, color: "#190122", marginBottom: 2 }}>{rec.title}</p>
                  <p style={{ fontFamily: "'Roboto', sans-serif", fontSize: 8.5, color: "#5a6270" }}>{rec.desc}</p>
                </div>
              ))}
            </div>

            <div className="w-full flex justify-center py-4">
              <div style={{ background: "rgba(9, 72, 93, 0.05)", padding: "8px 16px", borderRadius: 20 }}>
                <p style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: 10, color: "#09485d" }}>End of Report</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
